import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createSlots as _createSlots, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = { class: "payment-step-20676" }
const _hoisted_2 = {
  class: "payment-step-20676__submit btns btns--large",
  disabled: ""
}
const _hoisted_3 = {
  key: 0,
  class: "payment-step-20676__load-error"
}
const _hoisted_4 = ["disabled"]
const _hoisted_5 = { class: "payment-step-20676__disclosure" }
const _hoisted_6 = { key: 0 }
const _hoisted_7 = { key: 1 }
const _hoisted_8 = { key: 2 }

import { computed, ref } from 'vue'

import { useWait } from '@/app/composables/useWait'
import type { BillingView, PlanView } from '@/models/billing'
import { ValidationState } from '@/helpers/form'
import { Mixpanel } from '@/analytics/mixpanel'

import StripeCard from '@/app/components/billing/StripeCard.vue'
import FormError from '@/components/ui/FormError.vue'
import ButtonSpinner from '@/components/ui/ButtonSpinner.vue'


export default /*@__PURE__*/_defineComponent({
  __name: 'PaymentStep20676',
  props: {
    billing: {},
    plan: {}
  },
  emits: ["saved", "skip"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

const wait = useWait()
const stripeCard = ref<InstanceType<typeof StripeCard> | null>(null)
const validation = ref(new ValidationState())
const isStripeReady = ref(false)
// `elementsReady` never fires after a Stripe load error (network issue, ad
// blocker, etc.) -- without this, Skip would stay hidden forever alongside
// a payment form that can never work. See #20357 review discussion.
const hasStripeLoadError = ref(false)

const hasTrialDays = computed(() => true)

const trialEnd = computed(() => {
  return props.billing.trialEnd(props.plan.id)
})

const subscribePopupChargesText = computed(() => {
  if (props.plan.nickname === 'Annual' && props.plan.discount) {
    return (
      `charge the subscription fee of ${props.plan.price.perPeriod} to your payment ` +
      `method for your first ${props.plan.labels.period}, ` +
      `then ${props.plan.priceNoDiscount.perPeriod} ` +
      `for subsequent ${props.plan.labels.period}s on ${props.plan.labels.basis} basis until you cancel.`
    )
  }

  return (
    `charge the subscription fee of ${props.plan.price.perPeriod} ` +
    `to your payment method on ${props.plan.labels.basis} basis until you cancel.`
  )
})

async function startFreeTrial(): Promise<void> {
  try {
    let cardSuccess = true
    wait.start('Subscribing')
    if (props.billing.cardEditMode && stripeCard.value?.needsSaving()) {
      cardSuccess = await stripeCard.value.save()
    }
    if (cardSuccess) {
      await subscribe()
    }
    wait.end('Subscribing')
  } catch (error) {
    wait.end('Subscribing')
    validation.value.showErrors(error)
  }
}

/**
 * StripeCard's Express Checkout element (Apple Pay/Google Pay) saves the
 * card and emits `saved` on its own, outside the "Start Free Trial" button
 * flow.
 *
 * We check if the wait state is already active, and if so, we don't call `subscribe()`
 * again, since that would be a duplicate subscription attempt.
 */
async function onCardSaved(): Promise<void> {
  Mixpanel.trackOnboardingStartFreeTrial(props.plan.id)

  if (wait.is('Subscribing')) {
    return
  }

  try {
    wait.start('Subscribing')
    await subscribe()
    wait.end('Subscribing')
  } catch (error) {
    wait.end('Subscribing')
    validation.value.showErrors(error)
  }
}

async function subscribe(): Promise<void> {
  await props.billing.subscribeTo(props.plan)
  emit('saved')
  validation.value.reset()
}

/**
 * The `disabled` attribute above should already stop this, but that relies
 * on the DOM re-rendering in time -- guard here too so a user can't skip out
 * mid-payment via a duplicate/fast click.
 */
function onSkip(): void {
  if (wait.is('Subscribing')) {
    return
  }

  emit('skip')
}

return (_ctx: any,_cache: any) => {
  const _component_v_wait = _resolveComponent("v-wait")!
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", {
      class: _normalizeClass(["payment-step-20676__stripe-card", { 'payment-step-20676__stripe-card--ready': isStripeReady.value }])
    }, [
      _createVNode(StripeCard, {
        ref_key: "stripeCard",
        ref: stripeCard,
        billing: __props.billing,
        "show-button": false,
        onElementsReady: _cache[0] || (_cache[0] = ($event: any) => (isStripeReady.value = true)),
        onLoadError: _cache[1] || (_cache[1] = ($event: any) => (hasStripeLoadError.value = true)),
        onSaved: onCardSaved
      }, _createSlots({ _: 2 }, [
        (isStripeReady.value)
          ? {
              name: "actions",
              fn: _withCtx(() => [
                _createVNode(_component_v_wait, { for: "Subscribing" }, {
                  waiting: _withCtx(() => [
                    _createElementVNode("span", _hoisted_2, [
                      _cache[2] || (_cache[2] = _createTextVNode(" Processing... ", -1)),
                      _createVNode(ButtonSpinner)
                    ])
                  ]),
                  default: _withCtx(() => [
                    _createElementVNode("button", {
                      type: "button",
                      class: "payment-step-20676__submit btns btns--large",
                      onClick: startFreeTrial
                    }, " Start Free Trial ")
                  ]),
                  _: 1
                })
              ]),
              key: "0"
            }
          : undefined
      ]), 1032, ["billing"])
    ], 2),
    (hasStripeLoadError.value)
      ? (_openBlock(), _createElementBlock("p", _hoisted_3, " We couldn't load the payment form. Please try again later. "))
      : _createCommentVNode("", true),
    _createElementVNode("button", {
      type: "button",
      class: "payment-step-20676__skip",
      disabled: _unref(wait).is('Subscribing'),
      onClick: onSkip
    }, " Skip for now ", 8, _hoisted_4),
    _createVNode(FormError, {
      errors: validation.value.errors,
      field: __props.plan.id
    }, null, 8, ["errors", "field"]),
    _createElementVNode("p", _hoisted_5, [
      _cache[5] || (_cache[5] = _createTextVNode(" By clicking the \"Start Free Trial\" button above, you agree to our ", -1)),
      _createVNode(_component_router_link, {
        target: "_blank",
        to: { name: 'site_terms' }
      }, {
        default: _withCtx(() => [...(_cache[3] || (_cache[3] = [
          _createTextVNode("Terms of Use", -1)
        ]))]),
        _: 1
      }),
      _cache[6] || (_cache[6] = _createTextVNode(" and ", -1)),
      _createVNode(_component_router_link, {
        target: "_blank",
        to: { name: 'site_privacy' }
      }, {
        default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
          _createTextVNode("Privacy Policy", -1)
        ]))]),
        _: 1
      }),
      _cache[7] || (_cache[7] = _createTextVNode(". ", -1)),
      (hasTrialDays.value)
        ? (_openBlock(), _createElementBlock("span", _hoisted_6, "You may cancel any time in your free trial and you will not be charged. "))
        : _createCommentVNode("", true),
      _cache[8] || (_cache[8] = _createTextVNode("To cancel, go to Billing and click on \"Cancel subscription.\" ", -1)),
      _cache[9] || (_cache[9] = _createElementVNode("br", null, null, -1)),
      _cache[10] || (_cache[10] = _createElementVNode("br", null, null, -1)),
      (hasTrialDays.value)
        ? (_openBlock(), _createElementBlock("strong", _hoisted_7, " Shortform will automatically continue your subscription at the end of your free trial on " + _toDisplayString(trialEnd.value) + " and " + _toDisplayString(subscribePopupChargesText.value), 1))
        : (_openBlock(), _createElementBlock("strong", _hoisted_8, " Shortform will automatically continue your subscription and " + _toDisplayString(subscribePopupChargesText.value), 1)),
      _cache[11] || (_cache[11] = _createTextVNode(" There are no refunds or credits for partial subscription periods. ", -1))
    ])
  ]))
}
}

})